import { useCallback, useState } from "react";
import type { VisibilityState } from "@tanstack/react-table";

const LS_KEY = "chargersColumnConfig_v1";

const DEFAULT_VISIBILITY: VisibilityState = {
  status: true,
  name: true,
  product: true,
  voltage: true,
  current: true,
  session_energy: true,
  location: true,
  power_factor: false,
  protocol_version: false,
  unit_id_col: false,
};

// A saved config keeps every choice it holds; defaults fill in only the columns it does
// not mention (columns added after it was saved).
export function loadFromStorage(lsKey: string, defaults: VisibilityState): VisibilityState {
  try {
    const raw = localStorage.getItem(lsKey);
    if (raw) return { ...defaults, ...(JSON.parse(raw) as VisibilityState) };
  } catch {}
  return { ...defaults };
}

/**
 * Per-table column visibility, persisted to localStorage.
 *
 * Defaults to the chargers (Devices) table's key + defaults so existing call sites
 * keep working with no arguments; pass an `lsKey` and `defaults` to reuse the same
 * chooser for another table (e.g. the org Charging Sessions report). Each table MUST
 * use a distinct key so their saved choices don't collide.
 */
export function useColumnConfig(
  lsKey: string = LS_KEY,
  defaults: VisibilityState = DEFAULT_VISIBILITY,
) {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    loadFromStorage(lsKey, defaults),
  );

  const setVisibility = useCallback(
    (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
      setColumnVisibility((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        try {
          localStorage.setItem(lsKey, JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    [lsKey],
  );

  const resetToDefaults = useCallback(() => {
    try {
      localStorage.setItem(lsKey, JSON.stringify(defaults));
    } catch {}
    setColumnVisibility({ ...defaults });
  }, [lsKey, defaults]);

  return { columnVisibility, setColumnVisibility: setVisibility, resetToDefaults };
}
